"use client";
import { T, useTextLocale, tl } from "@/components/t";

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, FileText, Search, Briefcase, Kanban } from "lucide-react";
import styles from "./status.module.css";

// i18n 2026-07-25: this screen's copy lives in module-level config objects,
// which cannot call a hook. Rather than fork every interface, English is kept
// in a lookup keyed by the German source string and resolved at the render
// site with enCopy(loc, x) — unmapped values fall through.
const EN_COPY: Record<string, string> = {
  "Lebenslauf": "CV",
};
// Resolve config copy for the active locale. Takes `string | undefined` so
// optional config fields need no call-site guards; unmapped strings fall through.
function enCopy(loc: Parameters<typeof tl>[0], v?: string): string {
  return v ? tl(loc, v, EN_COPY[v] ?? v) : "";
}



export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const loc = useTextLocale();
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[careerkit] page error:", error);
  }, [error]);

  // Quick-jump tools so a single broken route doesn't strand the user.
  const tools: { href: string; label: string; icon: React.ElementType }[] = [
    { href: "/cv", label: "Lebenslauf", icon: FileText },
    { href: "/jobs", label: "Jobsuche", icon: Search },
    { href: "/apply", label: "Bewerbungen", icon: Briefcase },
    { href: "/tracker", label: "Tracker", icon: Kanban },
  ];

  return (
    <div className={styles.wrap}>
      <div className={styles.inner}>
        <span className={`${styles.tile} ${styles.tileError}`}>
          <AlertTriangle className="h-10 w-10" />
        </span>
        <div className={`${styles.eyebrow} ${styles.eyebrowError}`}>
          <T de={"Etwas ist schiefgelaufen"} en={"Something went wrong"} />
        </div>
        <h1 className={styles.h1}>
          <T de={"Diese Seite konnte nicht geladen werden."} en={"This page could not be loaded."} />
        </h1>
        <p className={styles.sub}>
          <T de="Andere Tools funktionieren weiterhin. Wenn der Fehler bleibt, melde dich bei" en="Other tools still work. If the error persists, contact" />{" "}
          <a href="mailto:support@studysmarter.de">
            support@studysmarter.de
          </a>
          {error.digest && (
            <>
              {" "}<T de="und gib bitte diese ID an:" en="and include this ID:" />{" "}
              <code className={styles.code}>{error.digest}</code>
            </>
          )}.
        </p>

        <div className={styles.actions}>
          <button onClick={reset} className={`${styles.btn} ${styles.btnPrimary}`}>
            <RefreshCw className="h-4 w-4" /> <T de="Erneut versuchen" en="Try again" />
          </button>
        </div>

        <div className={styles.jump}>
          <p className={styles.jumpLabel}><T de="Direkt weiter" en="Go straight to" /></p>
          <div className={styles.jumpGrid}>
            {tools.map((t) => {
              const Icon = t.icon;
              return (
                <Link key={t.href} href={t.href} className={styles.jumpCard}>
                  <span className={styles.jumpIcon}>
                    <Icon className="h-4 w-4" />
                  </span>
                  {enCopy(loc, t.label)}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
